import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { animeCategories, featuredAnime, latestAnime, popularAnime } from '@/mocks/anime';
import type { AnimeCategory } from '@/mocks/anime';

const seo = {
  title: '樱花动漫 - 专注动漫的网站 在线观看高清动漫',
  description: '樱花动漫网是一个专注于动漫的网站，让您在线观看高清动漫，无需注册或订阅，即可访问大量优质内容，随时随地沉浸在精彩的动漫世界中。热血战斗、奇幻冒险、恋爱日常、搞笑喜剧等海量分类。',
  keywords: '樱花动漫,动漫网站,高清动漫在线,动漫观看平台,热血动漫,奇幻动漫,恋爱动漫,搞笑动漫',
};

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const filteredAnime = selectedCategory === 'all'
    ? latestAnime
    : latestAnime.filter(a => a.categorySlug === selectedCategory);

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href="https://www.sakura-anime.com" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.sakura-anime.com" />
      <meta property="og:site_name" content="樱花动漫" />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />

      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{
        url: 'https://www.sakura-anime.com',
        name: '樱花动漫 - 首页',
        description: seo.description,
      }} />

      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20anime%20style%20illustration%20of%20a%20dreamy%20cherry%20blossom%20landscape%20with%20soft%20pink%20petals%20floating%20in%20golden%20sunlight%2C%20distant%20mountains%2C%20stylized%20artistic%20anime%20aesthetic%2C%20warm%20golden%20and%20soft%20pink%20color%20palette%2C%20cinematic%20composition%2C%20clean%20illustration&width=1800&height=900&seq=sakura-hero-bg&orientation=landscape"
            alt="樱花动漫 - 在线观看高清动漫"
            title="樱花动漫 高清动漫在线观看平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 w-full max-w-3xl">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
                沉浸在<span className="text-primary-400">樱花</span>动漫的世界
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                海量高清动漫在线观看，热血、奇幻、恋爱、搞笑，总有一部属于你的精彩
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/action"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  开始探索
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/category/fantasy"
                  className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white font-medium rounded-full backdrop-blur-sm transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  热门推荐
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-0 right-0 flex justify-center">
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <div className="flex items-center gap-1">
                <span className="font-bold text-white">1000+</span>
                <span>动漫资源</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <div className="flex items-center gap-1">
                <span className="font-bold text-white">6</span>
                <span>大分类</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-white/30"></span>
              <div className="flex items-center gap-1">
                <span className="font-bold text-white">免费</span>
                <span>无需注册</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                动漫分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                六大分类覆盖各类动漫，轻松找到你喜爱的类型
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {animeCategories.map((cat: AnimeCategory) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 text-center hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-white transition-colors duration-200">
                    <i className={`${cat.iconClass} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500">{cat.count} 部动漫</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  精选推荐
                </h2>
                <p className="text-foreground-600 text-sm">评分最高的人气动漫，每一部都值得一看</p>
              </div>
              <Link
                to="/category/action"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {featuredAnime.map((anime) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-xl overflow-hidden mb-3">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      title={`${anime.title} - 樱花动漫 高清在线观看`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                      {anime.rating}
                    </div>
                    {anime.status === '连载中' && (
                      <div className="absolute top-2 left-2 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                        {anime.status}
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{anime.category} · {anime.episodes}话</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                最新更新
              </h2>
              <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setSelectedCategory('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedCategory === 'all'
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  全部
                </button>
                {animeCategories.slice(0, 4).map((cat) => (
                  <button
                    key={cat.slug}
                    type="button"
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      selectedCategory === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAnime.map((anime, idx) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-background-100/70 transition-all duration-200 cursor-pointer"
                >
                  <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      title={`${anime.title} - 樱花动漫`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-accent-600 bg-accent-100 px-1.5 py-0.5 rounded-full font-medium">
                          TOP {idx + 1}
                        </span>
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {anime.title}
                        </h3>
                      </div>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{anime.description}</p>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-foreground-400">
                      <span>{anime.category}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                      <span>{anime.episodes} 话</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-300"></span>
                      <span>更新至 {anime.updateDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                热门排行
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                全网播放量最高的动漫排行榜
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularAnime.slice(0, 6).map((anime, idx) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-background-100/70 transition-all duration-200 cursor-pointer"
                >
                  <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg font-bold text-sm ${
                    idx === 0 ? 'bg-accent-500 text-white' :
                    idx === 1 ? 'bg-secondary-500 text-white' :
                    idx === 2 ? 'bg-primary-300 text-white' :
                    'bg-background-100 text-foreground-500'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      title={`${anime.title} - 樱花动漫热门推荐`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-foreground-500">{anime.category}</span>
                      <div className="flex items-center gap-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-[11px] text-foreground-600">{anime.rating}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-0.5">
                      {(anime.views / 10000).toFixed(0)}万 次观看
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-primary-500">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
              准备好开始你的动漫之旅了吗？
            </h2>
            <p className="text-white/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
              无需注册，无需订阅，打开即看。上千部高清动漫等你来发现。
            </p>
            <Link
              to="/category/action"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              立即观看
              <i className="ri-play-circle-line text-lg"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}